import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErpDashboard } from '../../erpApi';
import { formatCurrency } from '../../lib/formatters';
import {
  Euro, FileText, Hammer, AlertTriangle, Boxes, Users, Truck, Package, TrendingUp
} from 'lucide-react';

const Stat = ({ icon: Icon, label, value, color, onClick }) => (
  <button
    onClick={onClick}
    className={`text-left p-4 bg-white rounded-xl border border-slate-200 ${onClick ? 'hover:border-blue-400 cursor-pointer' : ''} transition-colors w-full`}
    data-testid={`erp-stat-${label.replace(/\s+/g, '-').toLowerCase()}`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-xl font-bold text-slate-900 tabular-nums leading-tight">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  </button>
);

const ErpDashboard = ({ onJump }) => {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  useEffect(() => { apiErpDashboard().then(setD).catch(() => setD(null)); }, []);
  if (!d) return <div className="text-center py-12 text-sm text-slate-500">{t('common.loading')}</div>;
  return (
    <div className="space-y-4" data-testid="erp-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Euro} label={t('common.total')} value={formatCurrency(d.revenue_month)} color="bg-emerald-100 text-emerald-700" onClick={() => onJump?.('documents')} />
        <Stat icon={FileText} label={t('erp.documents.typeInvoice')} value={d.invoices_count_month} color="bg-blue-100 text-blue-700" onClick={() => onJump?.('documents')} />
        <Stat icon={Hammer} label={t('erp.orders.title')} value={d.open_production_orders} color="bg-amber-100 text-amber-700" onClick={() => onJump?.('production')} />
        <Stat icon={AlertTriangle} label={t('common.error')} value={d.low_stock_alerts_count} color="bg-red-100 text-red-700" onClick={() => onJump?.('stock')} />
        <Stat icon={Boxes} label={t('erp.stock.title')} value={formatCurrency(d.stock_valuation)} color="bg-indigo-100 text-indigo-700" onClick={() => onJump?.('stock')} />
        <Stat icon={Users} label={t('common.customer')} value={d.partner_customers} color="bg-purple-100 text-purple-700" onClick={() => onJump?.('partners')} />
        <Stat icon={Truck} label={t('common.supplier')} value={d.partner_suppliers} color="bg-rose-100 text-rose-700" onClick={() => onJump?.('partners')} />
        <Stat icon={Package} label={t('common.products')} value={d.products_count} color="bg-slate-100 text-slate-700" onClick={() => onJump?.('products')} />
      </div>

      {d.low_stock_alerts?.length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-red-700" /><div className="font-semibold text-red-900 text-sm">{t('erp.stock.title')}</div></div>
          <div className="space-y-1">
            {d.low_stock_alerts.map(a => (
              <div key={a.product_id} className="text-sm flex items-center justify-between bg-white rounded px-3 py-1.5 border border-red-100">
                <div>
                  <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded mr-1.5">{a.sku}</span>
                  <span className="text-slate-700">{a.name}</span>
                </div>
                <div className="text-xs text-red-700 font-semibold tabular-nums">
                  {a.current_quantity} / {a.min_stock} {a.unit_of_measure}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {d.top_products?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-slate-500" /><div className="text-sm font-semibold">{t('common.products')}</div></div>
          <div className="space-y-1.5">
            {d.top_products.map((p, idx) => (
              <div key={p.product_id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5 last:border-0">
                <div className="flex items-center gap-2">
                  <span className="text-slate-400 text-xs font-medium w-5">{idx + 1}.</span>
                  <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{p.sku}</span>
                  <span className="text-slate-700">{p.name}</span>
                </div>
                <div className="font-semibold text-blue-700 tabular-nums">{formatCurrency(p.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ErpDashboard;
